import React from 'react';
import { DefaultButton, MessageBar, MessageBarType, Pivot, PivotItem, Stack, Toggle, TooltipHost } from '@fluentui/react';
import { IStorefrontSettings } from '../../../../models/Storefront';
import { ILayout, ILayoutResponsive } from '../layout';
import NumberField from '../../../Inputs/NumberField';
import StorefrontLayoutEditor from './StorefrontLayoutEditor';
import LayoutPreview from './LayoutPreview';
import { templatesFor } from './layoutTemplates';

// AC-951 / S3.3 + AC-957 / S4.4 — the header/footer layout section: the structural editor (S3.1) +
// element editor (S3.2) paired with a live preview (desktop/mobile), plus the responsive controls —
// per-column hide-on-mobile (in the editor) and an optional separate mobile layout. Region-aware;
// reads/writes the `layout` sub-key of headerConfig / footerConfig. An empty layout means the store
// keeps its default (legacy) header/footer — so this whole section is opt-in.

interface IProps {
    region: 'header' | 'footer';
    settings: IStorefrontSettings;
    layout?: ILayout;
    onChange: (layout: ILayout | undefined) => void;
}

const StorefrontLayoutSection: React.FC<IProps> = ({ region, settings, layout, onChange }) => {
    const active = !!layout && (layout.rows?.length ?? 0) > 0;
    const logo = region === 'footer' ? settings.footerLogo : settings.headerLogo;
    const title = settings.title || settings.name;
    const [previewMobile, setPreviewMobile] = React.useState(false);

    const updateLayout = (patch: Partial<ILayout>) => onChange({ ...(layout as ILayout), ...patch });
    const setMobile = (mobile?: ILayoutResponsive) => updateLayout({ mobile });

    const customMobile = layout?.mobile?.strategy === 'custom';
    const toggleCustomMobile = (on: boolean) => {
        if (!on) { setMobile(undefined); return; }
        // Seed mobile rows from the desktop rows (deep-cloned so the two edit independently).
        const seed = layout?.mobile?.rows?.length ? layout.mobile.rows : JSON.parse(JSON.stringify(layout?.rows ?? []));
        setMobile({ strategy: 'custom', breakpoint: layout?.mobile?.breakpoint, rows: seed });
    };

    return (
        <div>
            <div style={{ fontSize: 12, color: '#605e5c', margin: '0 0 12px' }}>
                Build a custom {region} as a grid of rows and columns. While this is empty the storefront
                uses the standard {region}; add a row to switch this {region} over to the grid.
            </div>

            {!active && (
                <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 11, color: '#605e5c', marginBottom: 6 }}>Start from a template, or build your own from scratch below.</div>
                    <Stack horizontal wrap tokens={{ childrenGap: 8 }}>
                        {templatesFor(region).map((t) => (
                            <TooltipHost key={t.key} content={t.description}>
                                <DefaultButton text={t.name} iconProps={{ iconName: 'GridViewMedium' }} onClick={() => onChange(t.build())} />
                            </TooltipHost>
                        ))}
                    </Stack>
                </div>
            )}

            {active && (
                <div style={{ marginBottom: 12 }}>
                    <Stack horizontal horizontalAlign='space-between' verticalAlign='center'>
                        <div style={{ fontSize: 11, color: '#605e5c' }}>Live preview (approximate)</div>
                        <Pivot selectedKey={previewMobile ? 'mobile' : 'desktop'} onLinkClick={(item) => setPreviewMobile(item?.props.itemKey === 'mobile')} styles={{ root: { marginBottom: 2 } }}>
                            <PivotItem itemKey='desktop' headerText='Desktop' itemIcon='TVMonitor' />
                            <PivotItem itemKey='mobile' headerText='Mobile' itemIcon='CellPhone' />
                        </Pivot>
                    </Stack>
                    <LayoutPreview
                        layout={layout as ILayout}
                        region={region}
                        logo={logo}
                        title={title}
                        barColor={settings.menuBarColor}
                        barTextColor={settings.menuBarTextColor}
                        mobile={previewMobile}
                    />
                    <MessageBar messageBarType={MessageBarType.info} styles={{ root: { marginTop: 8 } }}>
                        This {region} now renders from the layout below. Delete every row to return to the standard {region}.
                    </MessageBar>
                </div>
            )}

            {active && (
                <div style={{ margin: '0 0 12px' }}>
                    <Toggle
                        inlineLabel
                        label='Separate mobile layout'
                        checked={customMobile}
                        onChange={(_, c) => toggleCustomMobile(!!c)}
                        styles={{ root: { marginBottom: 4 } }}
                    />
                    <div style={{ fontSize: 11, color: '#605e5c' }}>
                        Off: on small screens the columns above automatically stack into one column (hide individual
                        columns with each column&rsquo;s &ldquo;Hide on mobile&rdquo; toggle). On: design a separate grid just for mobile.
                    </div>
                    {customMobile && (
                        <div style={{ marginTop: 10, paddingLeft: 12, borderLeft: '3px solid #e1dfdd' }}>
                            <NumberField
                                inline
                                width={220}
                                label='Mobile breakpoint'
                                suffix='px'
                                placeholder='700'
                                value={typeof layout?.mobile?.breakpoint === 'number' ? layout.mobile.breakpoint : undefined}
                                onChange={(v) => setMobile({ ...(layout?.mobile as ILayoutResponsive), strategy: 'custom', breakpoint: v })}
                            />
                            <div style={{ fontSize: 11, color: '#605e5c', margin: '4px 0 8px' }}>At or below this width, the mobile layout below is used instead. Default 700.</div>
                            <StorefrontLayoutEditor
                                layout={{ rows: layout?.mobile?.rows ?? [] }}
                                onChange={(ml) => setMobile({ ...(layout?.mobile as ILayoutResponsive), strategy: 'custom', rows: ml?.rows ?? [] })}
                            />
                        </div>
                    )}
                </div>
            )}

            <div style={{ fontSize: active ? 12 : undefined, fontWeight: active ? 600 : undefined, color: active ? '#323130' : undefined, margin: active ? '0 0 6px' : undefined }}>
                {active && 'Desktop layout'}
            </div>
            <StorefrontLayoutEditor layout={layout} onChange={onChange} />
        </div>
    );
};

export default StorefrontLayoutSection;
